import { useLayoutEffect, useState } from 'react';

import { HARNESS_FLAGS } from '../../../api/harness/harnessFlags';
import useHarnessFlag from '../../../api/harness/useHarnessFlag';
import { ADA_SCRIPT_ID, DECAGON_SCRIPT_ID } from '../../../constants';
import { useAda } from './useAdaChatbot';
import { useDecagonChatbot } from './useDecagonChatbot';

export type ChatVendor = 'ada' | 'decagon' | null;

// A remount of the landing page must not pick the other vendor once a
// script is already on the page.
const injectedVendor = (): ChatVendor => {
  if (document.getElementById(DECAGON_SCRIPT_ID)) return 'decagon';
  if (document.getElementById(ADA_SCRIPT_ID)) return 'ada';
  return null;
};

const isChatHiddenOn = (location: string) =>
  location.includes('/verification') || location.includes('/international');

export const useChat = (location: string, reportCount: number) => {
  const { enabled: decagonEnabled, isResolved } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.CP_DECAGON_CHAT,
  });
  // Neither vendor's script can be removed once injected, so the first choice
  // sticks (across remounts, via the injected script) to avoid a second widget.
  // The choice is committed from an effect so a discarded render (StrictMode,
  // concurrent rendering) can never lock in a vendor.
  const [chosenVendor, setChosenVendor] = useState<ChatVendor>(null);

  useLayoutEffect(() => {
    if (chosenVendor) return;
    const injected = injectedVendor();
    if (injected) {
      setChosenVendor(injected);
    } else if (isResolved) {
      setChosenVendor(decagonEnabled ? 'decagon' : 'ada');
    }
  }, [chosenVendor, isResolved, decagonEnabled]);

  let vendor = chosenVendor;
  if (vendor === 'decagon' && isChatHiddenOn(location)) vendor = null;

  useAda(location, reportCount, vendor === 'ada');
  useDecagonChatbot(vendor === 'decagon', reportCount);

  return vendor;
};
